<template>
  <div class="conversation-new" v-if="open">
    <header class="conversation-new-header">
      <button type="button" class="conversation-new-close" @click="onHeaderClose">
        <v-icon size="18" color="#666">
          {{ canGoBack ? "mdi-arrow-right" : "mdi-close" }}
        </v-icon>
      </button>
      <div class="conversation-new-title">
        {{ headerTitle }}
      </div>
      <button
        v-if="isMultiUserMode"
        type="button"
        class="conversation-new-submit-top"
        :class="{ 'is-ready': canSubmit && !busy }"
        :disabled="!canSubmit || busy"
        @click="submit"
      >
        <v-progress-circular
          v-if="busy"
          indeterminate
          size="14"
          width="2"
          color="#0066FF"
        />
        <span v-else>{{ $t("conversation-start") }}</span>
      </button>
      <span v-else class="conversation-new-header-spacer" />
    </header>

    <div v-if="isMultiUserMode" class="conversation-new-name-block">
      <label class="conversation-new-label" for="conversation-group-name">
        {{
          effectiveMode === "channel"
            ? $t("conversation-channel-name")
            : $t("conversation-group-name")
        }}
      </label>
      <input
        id="conversation-group-name"
        v-model="groupName"
        type="text"
        class="conversation-new-input"
        :class="{ 'is-invalid': !!fieldErrors.groupName }"
        :placeholder="$t('conversation-group-name-placeholder')"
        :disabled="busy"
      />
      <p v-if="fieldErrors.groupName" class="conversation-new-field-error">
        {{ fieldErrors.groupName }}
      </p>
    </div>

    <div v-if="isComposeHome" class="conversation-new-shortcuts">
      <button
        type="button"
        class="conversation-new-shortcut"
        :disabled="busy"
        @click="enterCreateMode('group')"
      >
        <span class="conversation-new-shortcut-icon is-group">
          <v-icon size="18" color="#fff">mdi-account-group</v-icon>
        </span>
        <span class="conversation-new-shortcut-label">{{ $t("newGroup") }}</span>
      </button>
      <button
        type="button"
        class="conversation-new-shortcut"
        :disabled="busy"
        @click="enterCreateMode('channel')"
      >
        <span class="conversation-new-shortcut-icon is-channel">
          <v-icon size="18" color="#fff">mdi-bullhorn</v-icon>
        </span>
        <span class="conversation-new-shortcut-label">
          {{ $t("conversation-new-channel") }}
        </span>
      </button>
    </div>

    <div class="conversation-new-personal-search">
      <div
        class="conversation-new-personal-search-shell"
        :class="{ 'is-focused': searchFocused, 'has-value': !!search }"
      >
        <v-icon size="16" :color="searchFocused ? '#0066FF' : '#8a8a8a'">
          mdi-magnify
        </v-icon>
        <input
          ref="personalSearchInput"
          v-model="search"
          type="text"
          class="conversation-new-personal-search-input"
          :placeholder="$t('conversation-search-users-hint')"
          :disabled="busy"
          @focus="onSearchFocus"
          @blur="onSearchBlur"
          @input="onSearchInput"
        />
        <button
          v-if="search"
          type="button"
          class="conversation-new-personal-search-clear"
          :disabled="busy"
          @mousedown.prevent
          @click="clearSearch"
        >
          <v-icon size="16" color="#666">mdi-close</v-icon>
        </button>
      </div>
    </div>

    <div
      v-if="isMultiUserMode && selectedUsers.length"
      class="conversation-new-selected-chips"
    >
      <button
        v-for="user in selectedUsers"
        :key="'sel-' + user.id"
        type="button"
        class="conversation-new-chip"
        :disabled="busy"
        @click="toggleUser(user)"
      >
        <span>{{ formatName(user) }}</span>
        <v-icon size="14" color="#6b7280">mdi-close</v-icon>
      </button>
    </div>
    <p
      v-if="isMultiUserMode && fieldErrors.members"
      class="conversation-new-field-error conversation-new-field-error-pad"
    >
      {{ fieldErrors.members }}
    </p>

    <div class="conversation-new-personal-list">
      <template
        v-if="!isMultiUserMode && searchFocused && !searchTrimmed && recentItems.length"
      >
        <div class="conversation-new-section-label">
          {{ $t("conversation-recent") }}
        </div>
        <button
          v-for="item in recentItems"
          :key="'recent-' + item.id"
          type="button"
          class="conversation-new-user-row"
          :disabled="busy"
          @mousedown.prevent
          @click="pickRecent(item)"
        >
          <div
            class="conversation-new-user-avatar"
            :style="{ background: item.avatarColor || avatarColor(item.id) }"
          >
            <img
              v-if="resolveAvatarUrl(item)"
              :src="resolveAvatarUrl(item)"
              :alt="item.title || ''"
            />
            <span v-else>{{ titleInitial(item.title) }}</span>
          </div>
          <div class="conversation-new-user-meta">
            <div class="conversation-new-user-name">{{ item.title }}</div>
            <div v-if="item.preview" class="conversation-new-user-sub">
              {{ item.preview }}
            </div>
          </div>
        </button>
      </template>

      <template v-else>
        <div class="conversation-new-section-label">
          {{
            searchTrimmed
              ? $t("conversation-search-users")
              : $t("users")
          }}
        </div>
        <button
          v-for="user in userList"
          :key="'user-' + user.id"
          type="button"
          class="conversation-new-user-row"
          :class="{ 'is-selected': isSelected(user.id) }"
          :disabled="busy"
          @click="onUserRowClick(user)"
        >
          <div
            class="conversation-new-user-avatar"
            :style="{ background: avatarColor(user.id) }"
          >
            <img
              v-if="resolveAvatarUrl(user)"
              :src="resolveAvatarUrl(user)"
              :alt="formatName(user)"
            />
            <span v-else>{{ titleInitial(formatName(user)) }}</span>
          </div>
          <div class="conversation-new-user-meta">
            <div class="conversation-new-user-name">{{ formatName(user) }}</div>
          </div>
          <v-icon
            v-if="isMultiUserMode && isSelected(user.id)"
            size="20"
            color="#0066FF"
          >
            mdi-check-circle
          </v-icon>
          <v-icon
            v-else-if="isMultiUserMode"
            size="20"
            color="#d1d5db"
          >
            mdi-checkbox-blank-circle-outline
          </v-icon>
        </button>
        <div
          v-if="usersLoading && !userList.length"
          class="conversation-new-personal-hint"
        >
          <v-progress-circular
            indeterminate
            size="20"
            width="2"
            color="#0066FF"
          />
        </div>
        <div
          v-else-if="!usersLoading && !userList.length"
          class="conversation-new-personal-hint"
        >
          {{ $t("no-data-found") }}
        </div>
        <div
          v-if="usersHasMore && userList.length"
          v-observe-visibility="onResultsVisibility"
          class="conversation-new-personal-more"
        >
          <v-progress-circular
            v-if="usersLoading"
            indeterminate
            size="16"
            width="2"
            color="#0066FF"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";

export default {
  name: "ConversationNewChat",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "personal",
    },
    busy: {
      type: Boolean,
      default: false,
    },
    recentItems: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      createMode: null,
      selectedUsers: [],
      groupName: "",
      submitted: false,
      search: "",
      searchFocused: false,
      userList: [],
      usersLoading: false,
      usersPage: 1,
      usersHasMore: false,
    };
  },
  computed: {
    isCompose() {
      return this.mode === "compose";
    },
    isComposeHome() {
      return this.isCompose && !this.createMode;
    },
    canGoBack() {
      return this.isCompose && !!this.createMode;
    },
    effectiveMode() {
      if (this.isCompose) {
        return this.createMode || "personal";
      }
      return this.mode;
    },
    isMultiUserMode() {
      return (
        this.effectiveMode === "group" || this.effectiveMode === "channel"
      );
    },
    headerTitle() {
      if (this.isComposeHome) {
        return this.$t("conversation-new");
      }
      if (this.effectiveMode === "group") {
        return this.$t("newGroup");
      }
      if (this.effectiveMode === "channel") {
        return this.$t("conversation-new-channel");
      }
      return this.$t("conversation-new-personal");
    },
    searchTrimmed() {
      return (this.search || "").trim();
    },
    selectedIds() {
      return this.selectedUsers.map(function (u) {
        return Number(u.id);
      });
    },
    fieldErrors() {
      if (!this.submitted) {
        return {};
      }
      var errors = {};
      if (!this.selectedUsers.length) {
        errors.members = this.isMultiUserMode
          ? this.$t("conversation-group-min-users")
          : this.$t("conversation-personal-one-user");
      }
      if (this.isMultiUserMode && !(this.groupName || "").trim()) {
        errors.groupName =
          this.effectiveMode === "channel"
            ? this.$t("conversation-channel-name-required")
            : this.$t("conversation-group-name-required");
      }
      return errors;
    },
    canSubmit() {
      if (!this.selectedUsers.length) {
        return false;
      }
      if (this.isMultiUserMode && !(this.groupName || "").trim()) {
        return false;
      }
      return true;
    },
  },
  watch: {
    open(val) {
      if (!val) {
        this.reset();
        return;
      }
      this.resetAndFetchUsers();
    },
    mode() {
      this.createMode = null;
      this.resetFormFields();
      if (this.open) {
        this.resetAndFetchUsers();
      }
    },
  },
  created() {
    this.debouncedSearch = debounce(this.resetAndFetchUsers, 350);
  },
  beforeDestroy() {
    if (this.debouncedSearch && this.debouncedSearch.cancel) {
      this.debouncedSearch.cancel();
    }
  },
  methods: {
    resetFormFields() {
      this.selectedUsers = [];
      this.groupName = "";
      this.submitted = false;
    },
    reset() {
      this.createMode = null;
      this.resetFormFields();
      this.search = "";
      this.searchFocused = false;
      this.userList = [];
      this.usersLoading = false;
      this.usersPage = 1;
      this.usersHasMore = false;
    },
    onHeaderClose() {
      if (this.canGoBack) {
        this.createMode = null;
        this.resetFormFields();
        return;
      }
      this.$emit("close");
    },
    enterCreateMode(mode) {
      if (mode !== "group" && mode !== "channel") {
        return;
      }
      this.createMode = mode;
      this.resetFormFields();
    },
    onSearchFocus() {
      this.searchFocused = true;
    },
    onSearchBlur() {
      this.searchFocused = false;
    },
    clearSearch() {
      this.search = "";
      this.resetAndFetchUsers();
    },
    onSearchInput() {
      this.debouncedSearch();
    },
    resetAndFetchUsers() {
      this.usersPage = 1;
      this.usersHasMore = true;
      this.userList = [];
      this.fetchUsers(false);
    },
    onResultsVisibility(visible) {
      if (visible) {
        this.fetchUsers(true);
      }
    },
    fetchUsers(append) {
      var self = this;
      if (this.usersLoading) {
        return;
      }
      if (append && !this.usersHasMore) {
        return;
      }

      var page = append ? this.usersPage : 1;
      this.usersLoading = true;
      var params = {
        select: "partners",
        page: page,
      };
      var q = this.searchTrimmed;
      if (q) {
        params.search = q;
      }

      this.$axios
        .get(this.$endpoint + "getdetail", { params: params })
        .then(function (response) {
          var body = response && response.data ? response.data : {};
          var list = Array.isArray(body.data) ? body.data : [];
          var mapped = list
            .map(function (row) {
              return self.mapPartnerItem(row);
            })
            .filter(Boolean);
          if (append) {
            mapped.forEach(function (item) {
              if (
                !self.userList.some(function (existing) {
                  return Number(existing.id) === Number(item.id);
                })
              ) {
                self.userList.push(item);
              }
            });
          } else {
            self.userList = mapped;
          }
          var lastPage = Number(body.last_page) || 1;
          var currentPage = Number(body.current_page) || page;
          self.usersHasMore = currentPage < lastPage;
          if (self.usersHasMore) {
            self.usersPage = currentPage + 1;
          }
        })
        .catch(function () {
          if (!append) {
            self.userList = [];
          }
          self.usersHasMore = false;
        })
        .finally(function () {
          self.usersLoading = false;
        });
    },
    mapPartnerItem(row) {
      if (!row || row.id == null) {
        return null;
      }
      return {
        id: row.id,
        firstname: row.firstname,
        lastname: row.lastname,
        name:
          row.name ||
          [row.firstname, row.lastname].filter(Boolean).join(" ").trim() ||
          String(row.id),
        uid: row.uid,
        avatar: row.avatar,
      };
    },
    formatName(user) {
      if (!user) {
        return "";
      }
      if (user.name) {
        return user.name;
      }
      return (
        [user.firstname, user.lastname].filter(Boolean).join(" ").trim() ||
        String(user.id || "")
      );
    },
    titleInitial(title) {
      var text = (title || "").trim();
      return text ? text.charAt(0) : "?";
    },
    resolveAvatarUrl(source) {
      if (!source) {
        return "";
      }
      if (typeof source === "string") {
        return source;
      }
      if (source.avatarUrl) {
        return String(source.avatarUrl);
      }
      var avatar = source.avatar;
      if (avatar && typeof avatar === "object") {
        return avatar.link || avatar.url || avatar.path || "";
      }
      if (typeof avatar === "string" && avatar) {
        return avatar;
      }
      return (
        source.avatar_url ||
        source.photo ||
        source.image ||
        source.profile_image ||
        source.link ||
        ""
      );
    },
    avatarColor(id) {
      var colors = [
        "#0066FF",
        "#00897B",
        "#7B61FF",
        "#FF6B35",
        "#E91E63",
        "#FF9800",
        "#4CAF50",
      ];
      var n = Number(id) || 0;
      if (!n) {
        return colors[0];
      }
      return colors[Math.abs(n) % colors.length];
    },
    isSelected(userId) {
      return this.selectedIds.indexOf(Number(userId)) !== -1;
    },
    toggleUser(user) {
      if (!user || user.id == null || this.busy) {
        return;
      }
      if (this.isSelected(user.id)) {
        this.selectedUsers = this.selectedUsers.filter(function (u) {
          return Number(u.id) !== Number(user.id);
        });
      } else {
        this.selectedUsers = this.selectedUsers.concat([user]);
      }
    },
    onUserRowClick(user) {
      if (this.isMultiUserMode) {
        this.toggleUser(user);
        return;
      }
      this.pickUser(user);
    },
    pickRecent(item) {
      if (!item || this.busy) {
        return;
      }
      this.$emit("select-existing", item);
    },
    pickUser(user) {
      if (!user || user.id == null || this.busy) {
        return;
      }
      this.$emit("create", {
        mode: "personal",
        userIds: [user.id],
        users: [user],
        message: "",
        groupName: "",
      });
    },
    submit() {
      this.submitted = true;
      if (!this.canSubmit || this.busy) {
        return;
      }
      var users = this.selectedUsers.slice();
      this.$emit("create", {
        mode: this.effectiveMode,
        userIds: users.map(function (u) {
          return u.id;
        }),
        users: users,
        message: "",
        groupName: (this.groupName || "").trim(),
      });
    },
  },
};
</script>

<style scoped>
.conversation-new {
  position: absolute;
  inset: 0;
  z-index: 8;
  background: #fff;
  display: flex;
  flex-direction: column;
  direction: rtl;
}
.conversation-new-header {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #fff;
  border-bottom: 1px solid #eee;
  flex: 0 0 auto;
}
.conversation-new-title {
  font-size: 15px;
  font-weight: 600;
  color: #111;
  text-align: center;
}
.conversation-new-header-spacer {
  width: 36px;
  height: 36px;
}
.conversation-new-close,
.conversation-new-submit-top {
  border: none;
  background: transparent;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  font-family: inherit;
}
.conversation-new-close {
  width: 36px;
  padding: 0;
}
.conversation-new-close:hover {
  background: #f3f4f6;
}
.conversation-new-submit-top {
  color: #aeb4bf;
  font-size: 13px;
  font-weight: 600;
  min-width: 78px;
}
.conversation-new-submit-top.is-ready {
  color: #0066ff;
}
.conversation-new-submit-top:disabled {
  cursor: default;
}
.conversation-new-name-block {
  flex: 0 0 auto;
  padding: 12px 14px 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.conversation-new-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.4;
}
.conversation-new-input {
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #111;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  padding: 10px 12px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.conversation-new-input::placeholder {
  color: #9ca3af;
}
.conversation-new-input:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.12);
}
.conversation-new-input.is-invalid {
  border-color: #ef4444;
}
.conversation-new-input:disabled {
  background: #f9fafb;
  color: #9ca3af;
}
.conversation-new-field-error {
  margin: 0;
  font-size: 12px;
  color: #dc2626;
  line-height: 1.4;
}
.conversation-new-field-error-pad {
  padding: 0 14px 6px;
}
.conversation-new-shortcuts {
  flex: 0 0 auto;
  padding: 6px 0 2px;
  border-bottom: 1px solid #f0f0f0;
}
.conversation-new-shortcut {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: right;
  font-family: inherit;
}
.conversation-new-shortcut:hover:not(:disabled) {
  background: #f8fafc;
}
.conversation-new-shortcut:disabled {
  cursor: default;
  opacity: 0.65;
}
.conversation-new-shortcut-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}
.conversation-new-shortcut-icon.is-group {
  background: #0066ff;
}
.conversation-new-shortcut-icon.is-channel {
  background: #ff6b35;
}
.conversation-new-shortcut-label {
  font-size: 13px;
  font-weight: 600;
  color: #111;
}
.conversation-new-personal-search {
  flex: 0 0 auto;
  padding: 10px 12px 8px;
  background: #fff;
}
.conversation-new-personal-search-shell {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f4f6;
  transition: box-shadow 0.15s ease, background 0.15s ease;
}
.conversation-new-personal-search-shell.is-focused {
  background: #fff;
  box-shadow: inset 0 0 0 1.5px #0066ff;
}
.conversation-new-personal-search-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  color: #111;
}
.conversation-new-personal-search-input::placeholder {
  color: #9ca3af;
}
.conversation-new-personal-search-clear {
  border: none;
  background: transparent;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.conversation-new-selected-chips {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 12px 8px;
}
.conversation-new-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 5px 10px;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  color: #111;
}
.conversation-new-chip:hover:not(:disabled) {
  background: #eef2ff;
  border-color: #c7d2fe;
}
.conversation-new-chip:disabled {
  cursor: default;
  opacity: 0.7;
}
.conversation-new-personal-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 0 12px;
  border-top: 1px solid #f0f0f0;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
}
.conversation-new-personal-list::-webkit-scrollbar {
  width: 4px;
}
.conversation-new-personal-list::-webkit-scrollbar-track {
  background: transparent;
}
.conversation-new-personal-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.16);
  border-radius: 999px;
}
.conversation-new-personal-list::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.28);
}
.conversation-new-section-label {
  padding: 10px 14px 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}
.conversation-new-user-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: right;
  font-family: inherit;
}
.conversation-new-user-row:hover:not(:disabled) {
  background: #f8fafc;
}
.conversation-new-user-row.is-selected {
  background: #f0f7ff;
}
.conversation-new-user-row:disabled {
  cursor: default;
  opacity: 0.65;
}
.conversation-new-user-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  flex: 0 0 auto;
  overflow: hidden;
}
.conversation-new-user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.conversation-new-user-meta {
  flex: 1 1 auto;
  min-width: 0;
}
.conversation-new-user-name {
  font-size: 13px;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-new-user-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #9aa0a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.conversation-new-personal-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 16px;
  font-size: 12px;
  color: #9aa0a6;
  text-align: center;
}
.conversation-new-personal-more {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 8px 0;
}
</style>
